'use client'

import { motion } from 'framer-motion'
import Link from 'next/link'
import { UtensilsCrossed, CalendarCheck, Truck, Check, ArrowRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import MenuEditorMockup from './MenuEditorMockup'
import BookingDashboardMockup from './BookingDashboardMockup'
import DeliveryPanelMockup from './DeliveryPanelMockup'
import '../../sass/landing/FeaturesSection.scss'

type FeatureKey = 'menu' | 'bookings' | 'delivery'

type FeatureVisual =
  | { kind: 'editor' }
  | { kind: 'dashboard' }
  | { kind: 'delivery' }

const tabs: Array<{ key: FeatureKey; icon: typeof UtensilsCrossed; visual: FeatureVisual }> = [
  { key: 'menu', icon: UtensilsCrossed, visual: { kind: 'editor' } },
  { key: 'bookings', icon: CalendarCheck, visual: { kind: 'dashboard' } },
  { key: 'delivery', icon: Truck, visual: { kind: 'delivery' } }
]

const bulletCounts: Record<FeatureKey, number> = {
  menu: 6,
  bookings: 4,
  delivery: 4
}

const FeaturesSection = () => {
  const t = useTranslations('LandingV2.features')

  return (
    <section id='features' className='features'>
      <div className='features__container'>
        <div className='features__header'>
          <span className='landing-v2__chip'>{t('chip')}</span>
          <h2>{t('heading')}</h2>

          <ul className='features__overview' aria-label={t('chip')}>
            {tabs.map(({ key, icon: Icon }) => (
              <li key={key} className={`features__overview-pill features__overview-pill--${key}`}>
                <span className='features__overview-icon' aria-hidden='true'>
                  <Icon size={18} strokeWidth={2} />
                </span>
                <span className='features__overview-label'>{t(`items.${key}.tab`)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className='features__main'>
          {tabs.map(({ key, icon: Icon, visual }, i) => (
            <motion.div
              key={key}
              id={`features-${key}`}
              className={`features__block${i % 2 === 1 ? ' features__block--reverse' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className='features__text'>
                <span className='features__block-eyebrow'>
                  <Icon size={14} /> {t(`items.${key}.eyebrow`)}
                </span>
                <h3 className='features__block-title'>{t(`items.${key}.title`)}</h3>
                <p className='features__block-desc'>{t(`items.${key}.description`)}</p>
                <ul className='features__list'>
                  {Array.from({ length: bulletCounts[key] }, (_, idx) => idx + 1).map((idx) => (
                    <li key={idx} className='features__list-item'>
                      <Check size={16} /> {t(`items.${key}.bullets.b${idx}`)}
                    </li>
                  ))}
                </ul>
                <Link href='/login/signup' className='features__link'>
                  {t('link')} <ArrowRight size={14} />
                </Link>
              </div>

              <div className='features__visual'>
                <div className='features__visual-glow features__visual-glow--tr' />
                <div className='features__visual-glow features__visual-glow--bl' />
                {visual.kind === 'editor' && <MenuEditorMockup />}
                {visual.kind === 'dashboard' && <BookingDashboardMockup />}
                {visual.kind === 'delivery' && <DeliveryPanelMockup />}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default FeaturesSection
